import { useCallback } from 'react';

import { useDepartments } from '../use-departments';
import { useStaffs } from '../use-staffs';

import { FunctionName, PermissionAction } from '@/lib/enums';

/**
 * Common hook for department and staffs management
 * Provides department options, staff loading, and staff filtering by department
 */
export const useDepartmentStaffs = (permissionAction: PermissionAction) => {
  const { departments, loading: departmentsLoading } = useDepartments(permissionAction);
  const {
    loading: staffsLoading,
    loadStaffsByDepartment,
    getStaffsByDepartment,
    clearStaffsByDepartment,
  } = useStaffs();

  // Get sales person options filtered by department
  const getSalesPersonOptions = useCallback(
    (departmentId: string) => {
      return getStaffsByDepartment(departmentId);
    },
    [getStaffsByDepartment]
  );

  // Handle department change - load staffs for selected department
  const handleDepartmentChange = useCallback(
    async (departmentId: string) => {
      if (departmentId) {
        try {
          await loadStaffsByDepartment('sale', departmentId, FunctionName.COMPANY_MANAGEMENT);
        } catch (error) {
          console.error('Error loading staffs:', error);
        }
      } else {
        // Clear staffs if no department selected
        clearStaffsByDepartment();
      }
    },
    [loadStaffsByDepartment, clearStaffsByDepartment]
  );

  return {
    departments,
    departmentsLoading,
    staffsLoading,
    getSalesPersonOptions,
    handleDepartmentChange,
  };
};
